/**
 * Group standings with FIFA 2026 tie-breakers.
 *
 * VERIFIED ORDER (FIFA 2026 regulations art. 13, checked against ESPN/FOX/NBC
 * coverage in June 2026 — differs from specs/02 which described the pre-2026
 * order): head-to-head FIRST among tied teams, then overall stats:
 *   1. points · 2. h2h points · 3. h2h goal diff · 4. h2h goals scored
 *   5. overall goal diff · 6. overall goals scored
 *   7. fair play (NOT modelable from scores -> skipped, documented)
 *   8. FIFA ranking (real committed table src/data/fifa-rankings.json; Elo is now only a
 *      fallback for a team missing from that table)
 *   9. seeded random draw (specs/02 §8 — flagged for the 🎲 UI icon)
 * The chain is data-driven so the order can be swapped by configuration.
 */
import { mulberry32, hashSeed } from "./rng.ts";
import fifaRankDoc from "../data/fifa-rankings.json";
import type {
  EloTable,
  GroupMatchResult,
  StandingLine,
  TeamCode,
  TieBreakRule,
} from "./types.ts";

/** Classement FIFA officiel (figé pour la CdM) : code → position mondiale (1 = meilleur).
 *  C'est le RÉEL dernier critère de départage (art. 13) ; l'Elo n'est plus qu'un repli
 *  pour une équipe absente de la table. Surchargeable via `RankOptions.fifaRanks` (tests). */
const DEFAULT_FIFA_RANKS: Readonly<Record<string, number>> = fifaRankDoc.ranks;

/** FIFA 2026 verified order (without points which always comes first). */
export const FIFA_2026_TIEBREAKERS: readonly TieBreakRule[] = [
  "h2hPoints",
  "h2hGoalDiff",
  "h2hGoalsFor",
  "goalDiff",
  "goalsFor",
  "fifaRank",
  "eloRank",
  "randomDraw",
];

/** Pre-2026 order as written in specs/02 (kept for reference/tests). */
export const SPECS02_TIEBREAKERS: readonly TieBreakRule[] = [
  "goalDiff",
  "goalsFor",
  "h2hPoints",
  "h2hGoalDiff",
  "h2hGoalsFor",
  "eloRank",
  "randomDraw",
];

export interface RankOptions {
  readonly tiebreakers?: readonly TieBreakRule[];
  /** Elo table used by the eloRank criterion (repli quand le rang FIFA manque). */
  readonly elos?: EloTable;
  /** Classement FIFA (code → position, 1 = meilleur) ; défaut = table committée. */
  readonly fifaRanks?: Readonly<Record<TeamCode, number>>;
  /** Seed material for the deterministic last-resort draw. */
  readonly drawSeed?: string;
}

export interface RankedTeam extends StandingLine {
  readonly rank: number;
  /** True when the seeded draw was needed to place this team (🎲 in the UI). */
  readonly decidedByDraw: boolean;
}

/** Accumulate played/points/goals for `teams` over `matches` (others ignored). */
export function computeTable(
  teams: readonly TeamCode[],
  matches: readonly GroupMatchResult[],
): Map<TeamCode, StandingLine> {
  const table = new Map<TeamCode, StandingLine>();
  const acc = new Map<TeamCode, { p: number; pts: number; gf: number; ga: number }>();
  for (const t of teams) acc.set(t, { p: 0, pts: 0, gf: 0, ga: 0 });
  for (const m of matches) {
    const home = acc.get(m.home);
    const away = acc.get(m.away);
    if (!home || !away) continue;
    const [gh, ga] = m.score;
    home.p++;
    away.p++;
    home.gf += gh;
    home.ga += ga;
    away.gf += ga;
    away.ga += gh;
    if (gh > ga) home.pts += 3;
    else if (ga > gh) away.pts += 3;
    else {
      home.pts++;
      away.pts++;
    }
  }
  for (const t of teams) {
    const a = acc.get(t);
    if (!a) continue;
    table.set(t, {
      team: t,
      played: a.p,
      points: a.pts,
      goalDiff: a.gf - a.ga,
      goalsFor: a.gf,
      goalsAgainst: a.ga,
    });
  }
  return table;
}

interface SortContext {
  readonly matches: readonly GroupMatchResult[];
  readonly elos: EloTable;
  readonly fifaRanks: Readonly<Record<TeamCode, number>>;
  readonly rng: () => number;
  readonly drawFlags: Set<TeamCode>;
}

/** Numeric key for a rule, higher = better. Returns null for randomDraw. */
function ruleKey(
  rule: TieBreakRule,
  line: StandingLine,
  h2h: Map<TeamCode, StandingLine>,
  elos: EloTable,
  fifaRanks: Readonly<Record<TeamCode, number>>,
): number | null {
  switch (rule) {
    case "h2hPoints":
      return h2h.get(line.team)?.points ?? 0;
    case "h2hGoalDiff":
      return h2h.get(line.team)?.goalDiff ?? 0;
    case "h2hGoalsFor":
      return h2h.get(line.team)?.goalsFor ?? 0;
    case "goalDiff":
      return line.goalDiff;
    case "goalsFor":
      return line.goalsFor;
    case "fifaRank": {
      // Position mondiale → clé décroissante (rang 1 = meilleur). Absente = 0 → ne
      // sépare pas, on retombe sur l'eloRank.
      const r = fifaRanks[line.team];
      return r != null ? 1000 - r : 0;
    }
    case "eloRank":
      return elos[line.team] ?? 0;
    case "randomDraw":
      return null;
  }
}

/**
 * Order a cluster of teams tied on points. When a criterion splits the cluster,
 * the chain is re-applied from the start on each remaining sub-cluster
 * (head-to-head criteria are then recomputed among the still-tied teams only).
 */
function breakTie(
  cluster: readonly StandingLine[],
  rules: readonly TieBreakRule[],
  ctx: SortContext,
): StandingLine[] {
  if (cluster.length <= 1) return [...cluster];
  const clusterTeams = cluster.map((l) => l.team);
  const h2h = computeTable(
    clusterTeams,
    ctx.matches.filter((m) => clusterTeams.includes(m.home) && clusterTeams.includes(m.away)),
  );

  for (const rule of rules) {
    if (rule === "randomDraw") {
      const shuffled = [...cluster];
      for (const line of shuffled) ctx.drawFlags.add(line.team);
      // Fisher–Yates with the seeded stream — deterministic for a given seed.
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(ctx.rng() * (i + 1));
        const a = shuffled[i];
        const b = shuffled[j];
        if (a !== undefined && b !== undefined) {
          shuffled[i] = b;
          shuffled[j] = a;
        }
      }
      return shuffled;
    }
    const keyed = cluster.map((line) => ({
      line,
      key: ruleKey(rule, line, h2h, ctx.elos, ctx.fifaRanks) ?? 0,
    }));
    const distinctKeys = new Set(keyed.map((k) => k.key));
    if (distinctKeys.size === 1) continue; // criterion does not separate anyone
    keyed.sort((a, b) => b.key - a.key);
    const out: StandingLine[] = [];
    let i = 0;
    while (i < keyed.length) {
      let j = i + 1;
      const current = keyed[i];
      while (j < keyed.length && keyed[j]?.key === current?.key) j++;
      const sub = keyed.slice(i, j).map((k) => k.line);
      // Sub-cluster still tied on this criterion: restart the whole chain on it.
      out.push(...(sub.length > 1 ? breakTie(sub, rules, ctx) : sub));
      i = j;
    }
    return out;
  }
  return [...cluster];
}

/** Rank the 4 teams of a group (or any team list) over the given matches. */
export function rankGroup(
  teams: readonly TeamCode[],
  matches: readonly GroupMatchResult[],
  options: RankOptions = {},
): RankedTeam[] {
  const rules = options.tiebreakers ?? FIFA_2026_TIEBREAKERS;
  const elos = options.elos ?? {};
  const fifaRanks = options.fifaRanks ?? DEFAULT_FIFA_RANKS;
  const seed = hashSeed(options.drawSeed ?? [...teams].sort().join("-"));
  const ctx: SortContext = {
    matches,
    elos,
    fifaRanks,
    rng: mulberry32(seed),
    drawFlags: new Set(),
  };
  const table = computeTable(teams, matches);
  const lines = teams
    .map((t) => table.get(t))
    .filter((l): l is StandingLine => l !== undefined);

  // 1) points clusters, descending
  const byPoints = new Map<number, StandingLine[]>();
  for (const line of lines) {
    const bucket = byPoints.get(line.points) ?? [];
    bucket.push(line);
    byPoints.set(line.points, bucket);
  }
  const ordered: StandingLine[] = [];
  for (const pts of [...byPoints.keys()].sort((a, b) => b - a)) {
    const cluster = byPoints.get(pts) ?? [];
    ordered.push(...breakTie(cluster, rules, ctx));
  }
  return ordered.map((line, idx) => ({
    ...line,
    rank: idx + 1,
    decidedByDraw: ctx.drawFlags.has(line.team),
  }));
}

/** Rank all groups at once. `matchesByGroup` may contain partial results. */
export function rankAllGroups(
  groups: Readonly<Record<string, readonly TeamCode[]>>,
  matchesByGroup: Readonly<Record<string, readonly GroupMatchResult[]>>,
  options: RankOptions = {},
): Record<string, RankedTeam[]> {
  const out: Record<string, RankedTeam[]> = {};
  for (const [letter, teams] of Object.entries(groups)) {
    const groupOptions: RankOptions = options.drawSeed
      ? { ...options, drawSeed: `${options.drawSeed}:${letter}` }
      : options;
    out[letter] = rankGroup(teams, matchesByGroup[letter] ?? [], groupOptions);
  }
  return out;
}

export interface ThirdPlaceLine extends StandingLine {
  readonly group: string;
  readonly decidedByDraw: boolean;
}

/**
 * Rank the 12 third-placed teams and return the best 8.
 * Official criteria: points, goal diff, goals scored, (fair play — skipped),
 * FIFA ranking (real table, Elo fallback), then seeded draw.
 */
export function rankThirds(
  thirds: ReadonlyArray<{ group: string; line: StandingLine }>,
  options: Pick<RankOptions, "elos" | "fifaRanks" | "drawSeed"> = {},
): ThirdPlaceLine[] {
  const elos = options.elos ?? {};
  const fifaRanks = options.fifaRanks ?? DEFAULT_FIFA_RANKS;
  const fr = (t: TeamCode): number => fifaRanks[t] ?? 999; // absente = pire rang
  const rng = mulberry32(
    hashSeed(options.drawSeed ?? thirds.map((t) => t.line.team).sort().join("-")),
  );
  const drawFlags = new Set<TeamCode>();
  const cmp = (a: { line: StandingLine }, b: { line: StandingLine }): number =>
    b.line.points - a.line.points ||
    b.line.goalDiff - a.line.goalDiff ||
    b.line.goalsFor - a.line.goalsFor ||
    fr(a.line.team) - fr(b.line.team) || // rang FIFA : plus petit = meilleur (a avant b)
    (elos[b.line.team] ?? 0) - (elos[a.line.team] ?? 0);

  // Stable sort first, then a seeded shuffle of each run of perfect ties —
  // deterministic for a given seed, independent of sort internals.
  const entries = [...thirds].sort(cmp);
  let i = 0;
  while (i < entries.length) {
    let j = i + 1;
    const current = entries[i];
    while (j < entries.length && current !== undefined && cmp(current, entries[j] as { line: StandingLine }) === 0) j++;
    if (j - i > 1) {
      const run = entries.slice(i, j);
      for (const e of run) drawFlags.add(e.line.team);
      for (let k = run.length - 1; k > 0; k--) {
        const m = Math.floor(rng() * (k + 1));
        const a = run[k];
        const b = run[m];
        if (a !== undefined && b !== undefined) {
          run[k] = b;
          run[m] = a;
        }
      }
      entries.splice(i, j - i, ...run);
    }
    i = j;
  }

  return entries.map(({ group, line }) => ({
    ...line,
    group,
    decidedByDraw: drawFlags.has(line.team),
  }));
}
